import { useTranslation } from 'react-i18next';
import { ReactElement } from 'react';
import { DrawerPanel } from '@dmc/ui';
import { CasFormFilePicker } from '@cas/components/inputs/cas-form-file-picker/CasFormFilePicker';
import { QueryTag, TagList } from '@casops/store/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import { FileUploadModel } from '@casops/store/apis/commonModels';
import useFileUploadForm from '@casops/components/layout/model-form/useFileUploadForm';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import { downloadBlob } from '@casops/shared/utils/fileDownloads';
import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import styles from './GeofenceImportPanel.module.scss';

type FormModel = FileUploadModel;

const endpointUrl = 'api/geofences';

const tagsToClear: TagDescription<QueryTag>[] = [
  { type: QueryTag.Geofence, id: TagList },
  { type: QueryTag.GeofenceGeoJson, id: TagList },
  QueryTag.GeofenceVersion,
  { type: QueryTag.GeofenceStyle, id: TagList }, // Imported Geofences are assigned the default style.
];

const initialValues: FormModel = {
  file: undefined,
};

const handleKmlTemplateDownload = () => {
  const kmlPolygonContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <Placemark>
      <name>The Pentagon</name>
      <Polygon>
        <extrude>1</extrude>
        <altitudeMode>relativeToGround</altitudeMode>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>
              -77.05788457660967,38.87253259892824,100
              -77.05465973756702,38.87291016281703,100
              -77.05315536854791,38.87053267794386,100
              -77.05552622493516,38.868757801256,100
              -77.05844056290393,38.86996206506943,100
              -77.05788457660967,38.87253259892824,100
            </coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>
  </Document>
</kml>`;
  downloadBlob(
    kmlPolygonContent,
    'kml-polygon-template.kml',
    'application/vnd.google-earth.kml+xml;charset=utf-8;',
  );
};

const handleCsvTemplateDownload = (): void => {
  const csvCircularContent =
    'Name,Latitude,Longitude,Radius\r\nWabtecCircle, 12.99791, 77.62645, 50';
  downloadBlob(csvCircularContent, 'csv-circular-template.csv', 'text/csv;charset=utf-8;');
};

export function GeofenceImportPanel(): ReactElement {
  const { t } = useTranslation();

  const formProps = useFileUploadForm<FormModel>({
    endpointUrl,
    tagsToClear,
    defaultValues: initialValues,
    successMessage: t('GeofencesImportSuccess'),
  });

  return (
    <DrawerPanel header={t('ImportGeofences')}>
      <ModelForm {...formProps} submitLabel={t('Import')}>
        <>
          <CasFormFilePicker
            control={formProps.control}
            name="file"
            accept=".csv,.kml"
            minHeight="200px"
          />
          <div className={styles.link}>
            <VdsButton variant="ghost" onClick={handleKmlTemplateDownload}>
              {t('DownloadKmlTemplate')}
              <VdsIcon slot="icon-left" svgIcon="download" />
            </VdsButton>
          </div>
          <div className={styles.link}>
            <VdsButton variant="ghost" onClick={handleCsvTemplateDownload}>
              {t('DownloadCsvTemplate')}
              <VdsIcon slot="icon-left" svgIcon="download" />
            </VdsButton>
          </div>
        </>
      </ModelForm>
    </DrawerPanel>
  );
}

export default GeofenceImportPanel;
